import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Sparkles, ShieldCheck, Zap, Clock, Rocket, Check, Users, TrendingUp, DollarSign, Tag, ArrowRight, Instagram, Music2, Heart, Eye } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CheckoutModal from "@/components/CheckoutModal";
import CustomBoostSlider from "@/components/CustomBoostSlider";
import { api } from "@/lib/api";

const basePackages = [
  { id: "pkg_1k",   followers: 1000,  amount: 4.99,   label: "1,000 Followers" },
  { id: "pkg_2_5k", followers: 2500,  amount: 9.99,   label: "2,500 Followers" },
  { id: "pkg_5k",   followers: 5000,  amount: 29.99,  label: "5,000 Followers" },
  { id: "pkg_10k",  followers: 10000, amount: 49.99,  label: "10,000 Followers" },
  { id: "pkg_25k",  followers: 25000, amount: 109.99, label: "25,000 Followers" },
  { id: "pkg_50k",  followers: 50000, amount: 149.99, label: "50,000 Followers", best_deal: true },
];

const PRODUCT_META = {
  followers: { label: "Followers", icon: Users, unit: "followers" },
  likes:     { label: "Likes",     icon: Heart, unit: "likes" },
  views:     { label: "Views",     icon: Eye,   unit: "views" },
};

const faqs = [
  {
    q: "Is this safe for my account?",
    a: "Yes. We never ask for your password. All you need is your public Instagram username.",
  },
  {
    q: "How fast will I see followers?",
    a: "Delivery typically begins within a few hours and continues gradually for a natural growth pattern.",
  },
  {
    q: "Do I need to provide my password?",
    a: "Never. We only need your public @username. If anyone asks you for a password, it's a scam.",
  },
  {
    q: "Are you affiliated with Instagram?",
    a: "No. BoostUp is an independent service and is not affiliated with, endorsed by, or officially connected with Instagram or Meta Platforms, Inc.",
  },
  {
    q: "Can I get a refund?",
    a: "If your order fails to deliver within 72 hours, we'll refund you in full. Reach out to support with your order session ID.",
  },
];

const howSteps = [
  { icon: Users, title: "Pick a package", body: "Choose how many followers you want — from 1K up to 50K.", accent: "pink" },
  { icon: Rocket, title: "Enter your @handle", body: "Tell us the public Instagram username. No password required, ever.", accent: "orange" },
  { icon: TrendingUp, title: "Watch it grow", body: "Delivery starts within hours, gradually for a natural pattern.", accent: "pink" },
];

export default function Landing() {
  const [platform, setPlatform] = useState("instagram"); // "instagram" | "tiktok"
  const [product, setProduct] = useState("followers");   // "followers" | "likes" | "views"
  const [packages, setPackages] = useState(() =>
    basePackages.map((p) => ({ ...p, platform: "instagram", product: "followers" }))
  );
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch packages for the current platform + product combo. All combos
  // are now supported (TikTok Likes/Views enabled at $6.99/1K).
  useEffect(() => {
    api
      .get(`/packages?platform=${platform}&product=${product}`)
      .then((r) => setPackages(r.data))
      .catch(() => {
        setPackages(
          basePackages.map((p) => ({
            ...p,
            platform,
            product,
          }))
        );
      });
  }, [platform, product]);

  const openCheckout = (pkg) => {
    setSelected({
      ...pkg,
      platform: pkg.platform || platform,
      product: pkg.product || product,
    });
    setModalOpen(true);
  };

  const isTikTok = platform === "tiktok";
  const productLabel = PRODUCT_META[product].label;
  const productUnit = PRODUCT_META[product].unit;
  // Best deal supersedes "most popular" for the current pricing table
  const bestDealId = "pkg_50k";
  const popularId = "pkg_10k";

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{
            backgroundImage: "url(https://images.pexels.com/photos/10075566/pexels-photo-10075566.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#030303]/70 via-[#030303]/90 to-[#030303]" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-32 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-xs uppercase tracking-[0.2em] font-bold text-white/70 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-[#FF007F]" /> Instant growth, zero password
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Grow your{" "}
              <span className={isTikTok ? "text-gradient-tiktok" : "text-gradient-neon"}>
                {isTikTok ? "TikTok" : "Instagram"}
              </span>
              <br /> the lazy way.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              Real-looking followers, delivered gradually. Pick a package, drop your handle, and let the numbers climb while you sleep.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#pricing"
                data-testid="hero-cta-pricing"
                className="bg-neon text-white font-bold px-7 py-4 rounded-full hover:scale-105 transition-transform glow-pink"
              >
                View Packages
              </a>
              <a
                href="#how"
                data-testid="hero-cta-how"
                className="border border-white/20 bg-white/5 text-white font-bold px-7 py-4 rounded-full hover:bg-white/10 transition"
              >
                How it works
              </a>
            </div>

            <div className="mt-12 flex items-center gap-8 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#FF007F]" /> No password needed
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#FF5E00]" /> Delivery in hours
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#FF007F]" /> 100% secure checkout
              </div>
            </div>
          </motion.div>
        </div>

        {/* Marquee strip */}
        <div className="border-y border-white/10 bg-[#0A0A0A]/60 backdrop-blur-2xl py-6 relative">
          <Marquee speed={40} gradient gradientColor="#030303" gradientWidth={80}>
            {["Trusted by creators", "50K+ orders delivered", "⭐ 4.9 / 5 average rating", "Real-looking accounts", "No password required", "72h delivery guarantee"].map((t, i) => (
              <span key={i} className="mx-10 text-white/60 uppercase text-xs tracking-[0.3em] font-bold">{t}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <div className="uppercase text-xs font-bold tracking-[0.2em] text-[#FF007F] mb-4">
              Three steps
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-tight">
              Stupidly simple.<br />
              <span className="text-gradient-neon">Ridiculously effective.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {howSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.25 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="border border-white/10 rounded-2xl p-8 bg-[#0A0A0A] hover:border-[#FF007F]/40 transition-colors"
                >
                  <div className={`w-12 h-12 rounded-xl grid place-items-center mb-6 ${step.accent === "pink" ? "bg-[#FF007F]/10 text-[#FF007F]" : "bg-[#FF5E00]/10 text-[#FF5E00]"}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="text-white/30 text-xs font-bold uppercase tracking-[0.2em] mb-2">
                    Step {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-2xl font-bold mb-3">{step.title}</h3>
                  <p className="text-white/60 leading-relaxed">{step.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-28 relative border-t border-white/10 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div className="max-w-xl">
              <div className="uppercase text-xs font-bold tracking-[0.2em] text-[#FF5E00] mb-4">
                Packages
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                Pick your <span className={isTikTok ? "text-gradient-tiktok" : "text-gradient-neon"}>boost</span>.
              </h2>
            </div>
            <p className="text-white/60 max-w-md">
              One-time payments, no subscriptions. Delivery begins within a few hours of checkout.
            </p>
          </div>

          {/* PLATFORM TOGGLE */}
          <div className="mb-5 flex justify-center">
            <div
              data-testid="platform-toggle"
              className="inline-flex items-center gap-1 p-1.5 rounded-full border border-white/10 bg-[#0A0A0A]"
            >
              <button
                data-testid="platform-toggle-instagram"
                onClick={() => setPlatform("instagram")}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  !isTikTok
                    ? "bg-neon text-white glow-pink"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Instagram className="w-4 h-4" />
                Instagram
              </button>
              <button
                data-testid="platform-toggle-tiktok"
                onClick={() => setPlatform("tiktok")}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  isTikTok
                    ? "bg-gradient-tiktok text-white glow-tiktok"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Music2 className="w-4 h-4" />
                TikTok
              </button>
            </div>
          </div>

          {/* PRODUCT TOGGLE */}
          <div className="mb-10 flex justify-center">
            <div
              data-testid="product-toggle"
              className="inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-[#050505]"
            >
              {[
                { key: "followers", Icon: Users },
                { key: "likes", Icon: Heart },
                { key: "views", Icon: Eye },
              ].map(({ key, Icon }) => {
                const active = product === key;
                return (
                  <button
                    key={key}
                    data-testid={`product-toggle-${key}`}
                    onClick={() => setProduct(key)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                      active
                        ? "bg-white text-black"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {PRODUCT_META[key].label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg, i) => {
              const isFlash = pkg.flash === true;
              const isBest = pkg.best_deal === true || pkg.id === bestDealId;
              const isPopular = pkg.id === popularId && !isFlash && !isBest;
              const isTk = false; // Pricing cards use the standard pink theme on all platforms
              const highlighted = isBest || isPopular;
              return (
                <motion.div
                  key={`${pkg.id}-${pkg.platform || platform}-${product}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.25 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`pricing-card-${pkg.id}`}
                  className={`relative rounded-2xl p-8 transition-all ${
                    isFlash
                      ? "bg-[#0A0A0A] border-2 border-[#A855F7] glow-purple"
                      : isBest
                      ? "bg-[#0A0A0A] border-2 border-[#FF5E00] glow-orange"
                      : isPopular && isTk
                      ? "bg-[#0A0A0A] border-2 border-[#00F2EA] glow-tiktok"
                      : isPopular
                      ? "bg-[#0A0A0A] border-2 border-[#FF007F] glow-pink"
                      : "bg-[#0A0A0A] border border-white/10 hover:border-white/25"
                  }`}
                >
                  {isFlash && (
                    <div className="absolute -top-3 left-8 bg-[#A855F7] text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full flex items-center gap-1">
                      <Zap className="w-3 h-3" fill="white" /> Flash Sale
                    </div>
                  )}
                  {isBest && (
                    <div
                      data-testid={`best-deal-badge-${pkg.id}`}
                      className="absolute -top-3 left-8 bg-[#FF5E00] text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3" fill="white" /> Best Deal
                    </div>
                  )}
                  {isPopular && (
                    <div className={`absolute -top-3 left-8 text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full ${isTk ? "bg-gradient-tiktok" : "bg-neon"}`}>
                      Most Popular
                    </div>
                  )}
                  <div className={`uppercase text-xs font-bold tracking-[0.2em] mb-4 ${isFlash ? "text-[#C084FC]" : isBest ? "text-[#FF5E00]" : "text-white/50"}`}>
                    {pkg.label}
                  </div>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className={`font-display text-5xl font-black tracking-tighter ${
                      isFlash ? "text-gradient-purple" : isBest ? "text-gradient-orange" : isTk ? "text-gradient-tiktok" : ""
                    }`}>
                      ${pkg.amount.toFixed(2)}
                    </span>
                  </div>
                  <ul className="space-y-3 mb-8 text-sm text-white/70">
                    <li className="flex gap-2">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${isFlash ? "text-[#A855F7]" : isBest ? "text-[#FF5E00]" : isTk ? "text-[#00F2EA]" : "text-[#FF007F]"}`} />
                      {pkg.followers.toLocaleString()} {productUnit}
                    </li>
                    <li className="flex gap-2">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${isFlash ? "text-[#A855F7]" : isBest ? "text-[#FF5E00]" : isTk ? "text-[#00F2EA]" : "text-[#FF007F]"}`} />
                      Natural gradual delivery
                    </li>
                    <li className="flex gap-2">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${isFlash ? "text-[#A855F7]" : isBest ? "text-[#FF5E00]" : isTk ? "text-[#00F2EA]" : "text-[#FF007F]"}`} />
                      No password required
                    </li>
                    <li className="flex gap-2">
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${isFlash ? "text-[#A855F7]" : isBest ? "text-[#FF5E00]" : isTk ? "text-[#00F2EA]" : "text-[#FF007F]"}`} />
                      {isFlash ? "Limited-time deal" : "72h delivery guarantee"}
                    </li>
                  </ul>
                  <button
                    data-testid={`buy-btn-${pkg.id}`}
                    onClick={() => openCheckout(pkg)}
                    className={`w-full font-bold py-3.5 rounded-full transition-all ${
                      isFlash
                        ? "bg-purple text-white hover:scale-[1.02] glow-purple"
                        : isBest
                        ? "bg-gradient-orange text-white hover:scale-[1.02] glow-orange"
                        : isPopular && isTk
                        ? "bg-gradient-tiktok text-white hover:scale-[1.02] glow-tiktok"
                        : isPopular
                        ? "bg-neon text-white hover:scale-[1.02] glow-pink"
                        : "bg-white text-black hover:bg-white/90"
                    }`}
                  >
                    {isFlash ? "Claim this deal" : `Get ${pkg.followers.toLocaleString()} ${productUnit}`}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOM SLIDER — followers only */}
      {product === "followers" && (
        <CustomBoostSlider platform={platform} onBuy={openCheckout} />
      )}

      {/* AFFILIATE */}
      <section id="affiliate" className="py-28 relative border-t border-white/10 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.25 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-3xl border border-[#FF007F]/30 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#1A0510] p-10 md:p-14 glow-pink"
          >
            <div
              className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-30 pointer-events-none"
              style={{ background: "radial-gradient(circle, #FF007F 0%, transparent 70%)" }}
            />
            <div
              className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ background: "radial-gradient(circle, #FF5E00 0%, transparent 70%)" }}
            />

            <div className="relative grid lg:grid-cols-5 gap-10 items-center">
              <div className="lg:col-span-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF007F]/30 bg-[#FF007F]/10 text-xs uppercase tracking-[0.2em] font-bold text-[#FF007F] mb-6">
                  <DollarSign className="w-3.5 h-3.5" /> Affiliate program
                </div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                  Got followers?<br />
                  <span className="text-gradient-neon">Earn while they grow.</span>
                </h2>
                <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
                  Create your own promo code. Share it. Earn{" "}
                  <strong className="text-white">$1–$30</strong> on every paid order — your friends save{" "}
                  <strong className="text-white">$1 off</strong> at checkout.
                </p>

                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/affiliate"
                    data-testid="affiliate-cta-primary"
                    className="bg-neon text-white font-bold px-7 py-4 rounded-full hover:scale-105 transition-transform glow-pink inline-flex items-center gap-2"
                  >
                    Become an affiliate <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/affiliate"
                    data-testid="affiliate-cta-secondary"
                    className="border border-white/20 bg-white/5 text-white font-bold px-7 py-4 rounded-full hover:bg-white/10 transition inline-flex items-center gap-2"
                  >
                    <Tag className="w-4 h-4" /> Log into dashboard
                  </Link>
                </div>

                <ol className="mt-10 grid sm:grid-cols-3 gap-4 text-sm">
                  {[
                    { n: "01", t: "Pick a code", d: "Sign up, choose your custom promo code." },
                    { n: "02", t: "Share it", d: "Drop it in your bio, story, or DMs." },
                    { n: "03", t: "Get paid", d: "Cash out via DM once you hit $20." },
                  ].map((s) => (
                    <li key={s.n} className="border border-white/10 rounded-xl p-4 bg-black/30">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF007F] font-bold mb-2">
                        Step {s.n}
                      </div>
                      <div className="font-display text-lg font-bold mb-1">{s.t}</div>
                      <div className="text-white/50 text-xs leading-relaxed">{s.d}</div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-black/40 backdrop-blur border border-white/10 rounded-2xl p-6">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5E00] mb-4 flex items-center gap-2">
                    <TrendingUp className="w-3 h-3" /> Commission rates
                  </div>
                  <ul className="space-y-2.5 text-sm">
                    {[
                      ["1,000 Followers", "$1"],
                      ["2,500 Followers", "$2"],
                      ["5,000 Followers", "$5"],
                      ["10,000 Followers", "$10"],
                      ["25,000 Followers", "$20"],
                      ["50,000 Followers", "$30"],
                    ].map(([label, rate]) => (
                      <li key={label} className="flex justify-between items-center border-b border-white/5 pb-2 last:border-0">
                        <span className="text-white/70">{label}</span>
                        <span className="text-gradient-neon font-bold text-lg">{rate}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-white/40 mt-4 text-center">
                  No minimum follower count. No application. Just sign up and start sharing.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ */}
      <section id="faq" className="py-28 relative border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="uppercase text-xs font-bold tracking-[0.2em] text-[#FF007F] mb-4">
              Questions
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
              Quick answers.
            </h2>
          </div>
          <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border border-white/10 bg-[#0A0A0A] rounded-xl px-5"
              >
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-semibold hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/60 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />

      <CheckoutModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        pkg={selected}
      />
    </div>
  );
}
